import React from 'react';
import { Link } from 'react-router';
import { ChevronDown } from 'lucide-react';
import type { NavGroup } from '../content/headerNav';

/**
 * Desktop dropdown for one header group.
 *
 * Opens on hover for pointer users and on click/Enter for everyone, and closes
 * on Escape, on outside click and on selection. The panel is mounted only while
 * open, so it stays out of the tab order the rest of the time.
 *
 * A MOUSE CLICK OPENS; IT DOES NOT TOGGLE. Moving the pointer onto the trigger
 * fires `mouseenter` and opens the panel, so a plain toggle then closed the
 * panel the same gesture had just opened -- clicking a menu label appeared to do
 * nothing. Clicking a label can only mean "show me this", and the panel is
 * already showing, so the click is a no-op. Closing still has three routes:
 * moving the pointer away, Escape, and clicking outside.
 *
 * A keyboard activation still toggles, because for a keyboard there is no
 * mouseenter to have opened it and Enter on a disclosure is expected to work
 * both ways. `event.detail` is 0 for a click synthesised from Enter or Space and
 * non-zero for a real pointer click, which is what separates the two.
 *
 * HOVER IS GATED ON `(hover: hover)`. A touchscreen at desktop width — a touch
 * laptop, a tablet with a keyboard — synthesises `mouseenter` from the tap, so
 * with the handlers always attached the tap opened the panel and the click event
 * from the same tap immediately toggled it shut. Tapping the trigger did
 * nothing at all. Without hover handlers the click alone drives it.
 *
 * The items are `Link`s. They used to be buttons calling the header's own
 * navigate handler, for the site-wide scroll-to-top-then-navigate behaviour --
 * but `useRouteScroll` already scrolls to the top on every hashless route
 * change, so a plain link behaves identically. What the buttons cost was real:
 * no crawler could follow the menu, which left /about, /blog, /for-employers
 * and /ai-testing-use-cases with no inbound link from anywhere on the site, and
 * no visitor could cmd-click a menu item to open it in a new tab.
 *
 * The trigger stays a `<button>`: it opens a panel, it does not navigate.
 */
export function NavDropdown({
  group,
  isActive,
}: {
  group: NavGroup;
  /** True when the current route is one of the group's pages. */
  isActive: boolean;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const closeTimer = React.useRef<number | undefined>(undefined);

  /**
   * Read once on mount rather than at render: it decides whether the hover
   * handlers are attached at all, and swapping that mid-interaction would strand
   * an open panel. Defaults to true so SSR/no-matchMedia keeps hover.
   */
  const [canHover] = React.useState(
    () => typeof window === 'undefined' || window.matchMedia('(hover: hover)').matches
  );

  // Closing is delayed so the diagonal travel from the trigger to the panel
  // does not dismiss the menu on the way.
  const openNow = () => {
    window.clearTimeout(closeTimer.current);
    setIsOpen(true);
  };
  const closeSoon = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setIsOpen(false), 260);
  };

  React.useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={canHover ? openNow : undefined}
      onMouseLeave={canHover ? closeSoon : undefined}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={(event) => setIsOpen((open) => (canHover && event.detail > 0 ? true : !open))}
        className={`flex items-center gap-1 whitespace-nowrap px-3 py-2 rounded-md text-[15px] font-medium bg-transparent border-0 qa-nav-item ${
          isActive || isOpen
            ? 'text-qa-blue-600 bg-qa-blue-50'
            : 'text-muted-foreground hover:bg-qa-blue-50'
        }`}
      >
        {group.label}
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full pt-2 z-50">
          <ul className="w-[340px] list-none rounded-dropdown border border-border bg-background p-2 shadow-dropdown">
            {group.items.map((item) => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className="block w-full cursor-pointer rounded-item border-0 bg-transparent px-3 py-2.5 text-left transition-colors hover:bg-qa-blue-50"
                >
                  <span className="block text-[15px] font-semibold text-foreground">
                    {item.label}
                  </span>
                  {item.note && (
                    <span className="mt-0.5 block text-caption-sm text-muted-foreground">
                      {item.note}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
